import type { ReactElement } from 'react';
import { useCallback } from 'react';
import type { AuthValue, LanguageCode } from '@elfsight/configurator-contracts';
import { getSiteURL } from '@elfsight/widget-utils';
import type {
  SMTPIntegrationProps,
  SubmitActionType
} from '../../configurator';
import { DATA_SERVICE_URL } from '../../constants';
import type { Field, FieldType, FormField, FormStatus } from '../contexts';
import { useFormSelector } from '../contexts';
import type { MailchimpField, Webhook } from '../integrations';
import {
  usePageRedirect,
  useSubmitMessage,
  useWidgetClose
} from '../submit-actions';
import type { UrlParamProps } from '../url-params';
import { useUrlParams } from '../url-params';
import { FormContainer } from './FormContainer';
import { formSettings } from './formSettings';
import type { ConditionalLogicChangedFields } from './hooks';
import { useConditionalLogic } from './hooks';
import type { FormatSubmitData } from './utils';

export const EXCLUDED_FIELDS_FROM_SUBMIT = [
  'label',
  'heading',
  'paragraph'
] as FieldType[];

const SUBMIT_URL = `${DATA_SERVICE_URL}/public/resources/form-submissions/`;

export const DEFAULT_ERROR_ON_SUBMIT =
  'An error occurred when submitting the form, please try again later';

export interface FormProps {
  children: ReactElement;
  fields: FormField[];
  widgetToken: string | undefined;
  mailEI?: string;
  autoresponderEI?: string;
  editMode?: boolean;
  widgetLanguage?: LanguageCode;
  mailActive?: boolean;
  autoresponderActive?: boolean;
  submitAction?: SubmitActionType;
  submitRedirectURL?: string;
  submitUrl?: string;
  submitAdditionalData?: object;
  urlParams?: UrlParamProps[];
  recaptcha?: boolean;
  webhooks: Webhook[];
  integrationSMTP?: SMTPIntegrationProps;
  integrationSettingsID?: string;
  integrationMailchimp?: boolean;
  integrationMailchimpAuth?: AuthValue;
  integrationMailchimpList?: string;
  integrationMailchimpFieldsMap?: MailchimpField[];
  integrationGoogleSheets?: boolean;
  integrationGoogleSheetsAuth?: AuthValue;
  integrationGoogleSheetsId?: string;
  submitError?: string;
  consentValues?: [yes: string, no: string];
  integrationGoogleSheetsTitle?: string;
  formatSubmitData?: FormatSubmitData;

  onSubmitSuccessful?(submitAction: SubmitActionType): void;
}

export function Form({
  children,
  fields,
  widgetToken,
  mailEI,
  autoresponderEI,
  editMode = false,
  mailActive = false,
  autoresponderActive = false,
  submitAction = 'message',
  submitRedirectURL = '',
  submitUrl = SUBMIT_URL,
  submitAdditionalData,
  urlParams = formSettings.urlParams,
  integrationSMTP,
  integrationSettingsID,
  submitError = DEFAULT_ERROR_ON_SUBMIT,
  integrationMailchimp = formSettings.integrationMailchimp,
  integrationMailchimpAuth = formSettings.integrationMailchimpAuth,
  integrationMailchimpList: listID = formSettings.integrationMailchimpList,
  integrationMailchimpFieldsMap:
    fieldsMapping = formSettings.integrationMailchimpFieldsMap,
  integrationGoogleSheets = formSettings.integrationGoogleSheets,
  integrationGoogleSheetsAuth = formSettings.integrationGoogleSheetsAuth,
  integrationGoogleSheetsId = formSettings.integrationGoogleSheetsId,
  onSubmitSuccessful,
  ...forwardingProps
}: FormProps) {
  const { fields: changedFields } = useFormSelector();
  const displayedFields = useConditionalLogic(
    fields,
    changedFields as ConditionalLogicChangedFields
  );

  const getNotificationIntegration = useCallback(
    (ei?: string) => {
      const isConnectedSMTP =
        integrationSMTP?.status === 'connected' && integrationSMTP?.pid;

      return {
        pageUrl: getSiteURL('page'),
        websiteUrl: getSiteURL('site'),
        ei,
        ...(isConnectedSMTP ? { smtpSettingsPid: integrationSMTP.pid } : {})
      };
    },
    [integrationSMTP]
  );

  const selfNotificationIntegration = getNotificationIntegration(mailEI);
  const respondentNotificationIntegration =
    getNotificationIntegration(autoresponderEI);

  const emailField = fields.filter(({ type }) => type === 'email')[0]?.id;
  const hasEmailValue = Object.entries(changedFields).some(
    ([_, { type, value }]: [string, Field]) =>
      type === 'email' ? !!value : false
  );

  const mailchimpIntegration = {
    identity: integrationMailchimpAuth?.identity,
    listID,
    emailField,
    fieldsMapping: fieldsMapping
      .map(
        ({ from, to }) =>
          to && {
            from,
            to
          }
      )
      .filter(Boolean)
  };

  const googleSheetsIntegration = {
    identity: integrationGoogleSheetsAuth?.identity,
    sheetId: integrationGoogleSheetsId
  };

  const integrations = [
    {
      provider: 'mail',
      active: mailActive,
      options: selfNotificationIntegration
    },
    {
      provider: 'mail_autoresponder',
      active: autoresponderActive && hasEmailValue,
      options: respondentNotificationIntegration
    },
    {
      provider: 'mailchimp',
      active: integrationMailchimp && hasEmailValue,
      options: mailchimpIntegration
    },
    {
      provider: 'google',
      active: integrationGoogleSheets,
      options: googleSheetsIntegration
    }
  ]
    .filter((integration) => integration.active)
    .map(({ provider, options }) => ({ provider, options }));

  const additionalData = {
    widget_token: widgetToken,
    settings_pid: integrationSettingsID,
    integrations,
    ...submitAdditionalData
  };

  useUrlParams(urlParams, fields);

  const actionHandlers: ActionHandlers = {
    redirect: usePageRedirect(submitRedirectURL, fields),
    hide: useWidgetClose(editMode, fields),
    message: useSubmitMessage()
  };

  const handleSubmit = async (submit: () => Promise<FormStatus>) => {
    try {
      const submitStatus = await submit();

      if (['failed', 'invalid'].includes(submitStatus)) {
        throw new Error();
      }

      if (submitStatus !== 'success') {
        return;
      }

      if (onSubmitSuccessful) {
        onSubmitSuccessful(submitAction);
      }

      actionHandlers[submitAction]();

      return;
    } catch (error: any) {
      if (error.invalid) {
        return;
      }

      return submitError;
    }
  };

  return (
    <FormContainer
      {...forwardingProps}
      fields={fields}
      displayedFields={displayedFields}
      submitUrl={submitUrl}
      additionalData={additionalData}
      onSubmit={handleSubmit}
    >
      {children}
    </FormContainer>
  );
}

type ActionHandlers = Record<string, () => void>;
